import React, { useState, useEffect } from "react";
import { Download, X } from "lucide-react";
import { getReportTypes, getAvailableYear, getTemplate } from "../../services/complianceService";
import { useSelector } from "react-redux";

const ExportToExcel = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [reportFormats, setReportFormats] = useState([]);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    if (showPopup) {
      fetchDropdownData();
    }
  }, [showPopup]);

  const fetchDropdownData = async () => {
    setLoading(true);
    try {
      const reportTypesRes = await getReportTypes();
      if (reportTypesRes.status === 'success') {
        setReportFormats(reportTypesRes.data);
      }

      const yearsRes = await getAvailableYear();
      if (yearsRes.status === 'success') {
        setYears(yearsRes.data);
      }
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportClick = () => {
    setShowPopup(true);
  };

  const downloadFile = (blob, filename) => {
    // Create a temporary URL for the blob
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Set the filename with proper extension
    const fileExtension = filename.includes('.xlsm') ? '.xlsm' : '.xlsx';
    link.download = filename.endsWith(fileExtension) ? filename : `${filename}${fileExtension}`;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportConfirm = async () => {
    if (!selectedFormat || !selectedYear) return;

    setExportLoading(true);
    try {
      console.log("Exporting with:", {
        format: selectedFormat,
        year: selectedYear
      });

      const response = await getTemplate(
        selectedFormat,
        selectedYear,
        currentVessel._id
      );

      // Check if response is blob (file) or JSON (error)
      if (response.data instanceof Blob) {
        // Get filename from response headers or create one
        const contentDisposition = response.headers['content-disposition'];
        let extension = selectedFormat === "ABS_ERP" ? "xlsx" : "xlsm";
        let filename = `${selectedFormat}_${selectedYear}.${extension}`;

        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
          }
        }

        // Download the file
        downloadFile(response.data, filename);
        
        // Show success message
        console.log('File downloaded successfully');
      } else {
        // Handle JSON response (error or success message)
        console.log('Server response:', response.data);
      }

    } catch (err) {
      console.error("Error downloading template:", err);
      
      // Handle specific error cases
      if (err.response) {
        if (err.response.status === 404) {
          console.error('Template not found');
        } else if (err.response.status === 500) {
          console.error('Server error during template generation');
        }
      }
    } finally {
      setExportLoading(false);
      setShowPopup(false);
      setSelectedFormat("");
      setSelectedYear("");
    }
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedFormat("");
    setSelectedYear("");
  };

  return (
    <>
      {/* Export Button */}
      <button
        onClick={handleExportClick}
        className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white text-gray-800 border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
      >
        <Download className="w-4 h-4" />
        <span>Export to Excel</span>
      </button>

      {/* Export Popup */}
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Export to Excel
              </h3>
              <button
                onClick={handleClosePopup}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={exportLoading}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Report Format Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Report Format
                </label>
                <select
                  value={selectedFormat}
                  onChange={(e) => setSelectedFormat(e.target.value)}
                  disabled={loading || exportLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                >
                  <option value="">{loading ? "Loading..." : "Select format"}</option>
                  {reportFormats.map((format) => (
                    <option key={format.code} value={format.code}>
                      {format.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Year Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Year
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  disabled={loading || exportLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                >
                  <option value="">{loading ? "Loading..." : "Select year"}</option>
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={handleClosePopup}
                disabled={exportLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleExportConfirm}
                disabled={!selectedFormat || !selectedYear || loading || exportLoading}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#27ade3] border border-transparent rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {exportLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Export
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExportToExcel;